import { useCallback, useEffect, useRef, useState } from "react";
import type { ScheduleEntry } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { markNote, overlapNote } from "./common.ts";
import { movedGroupOccurrenceText } from "./group-text.ts";
import { dropDoneText, dropErrorText, wallZone, type DropAction, type DropPlan } from "./entry-drag.ts";

/**
 * Всплывающая полоса о том, что вышло из переноса, с кнопкой «Вернуть».
 *
 * ⚠️ **Решение владельца 10.09.2026: ступени подтверждения у жеста нет ни у
 * кого, включая занятие группы.** До этого дня у группы отпускание спрашивало
 * («Занятие переедет у всех в группе: 6 учеников», кнопки «Перенести» и «Не
 * надо») по правилу «цена массового действия читается до нажатия». Правило не
 * отменено, оно взвешено: вопрос на каждое движение съедал ровно то, ради чего
 * жест заведён, а перенос обратим одним нажатием и ничего не разрушает, в
 * отличие от отмены занятия, где вторая ступень остаётся. Цена группы теперь
 * называется в полосе, второй строкой, рядом с кнопкой возврата.
 *
 * ⚠️ **Полоса не забирает фокус и ничего не закрывает собой.** Раньше на её
 * месте стояло окно у точки отпускания: оно вставало под курсором, требовало
 * закрытия и читалось как подтверждение уже сделанного. Отсюда `aria-live`:
 * читалке новость достаётся словами, а не переносом фокуса.
 *
 * ⚠️ **Кнопка называется «Вернуть», а не «Отменить», и это не придирка.** В
 * этом продукте отмена занятия это отдельное действие, которое снимает занятие у
 * обеих сторон; кнопка «Отменить» рядом с занятием читается именно так.
 *
 * Проект целиком в `docs/schedule-drag-design.md`, раздел 7.
 */

/**
 * Сколько полоса живёт, если всё вышло.
 *
 * Двенадцать секунд, а не пять: столько нужно, чтобы прочитать, куда занятие
 * встало, увидеть накладку и решить, возвращать ли. Отказ не гаснет вовсе:
 * исчезнувшая причина отказа означает «ничего не произошло», а произошло.
 */
const DONE_MS = 12_000;

/**
 * Позвать ту ручку, которая полагается виду строки.
 *
 * ⚠️ Адрес берётся у строки, которую ВЗЯЛИ рукой, а не у перечитанной: ключ
 * занятия при переносе не меняется никогда, а вот исчезнуть из недели строка
 * может, и тогда возвращать было бы нечем.
 */
async function send(entry: ScheduleEntry, action: DropAction, zone: string): Promise<void> {
  if (action.kind === "none") return;
  if (entry.standalone) {
    // У разового занятия переезжает сам урок, исключений у него нет вовсе,
    // поэтому ветки `clear` тут не бывает (`dropPlan` её не производит)
    if (entry.lessonId === null || action.kind !== "move") return;
    await api.moveSingleLesson(entry.lessonId, {
      date: action.date,
      time: action.time,
      timeZone: wallZone(entry, zone),
    });
    return;
  }
  if (entry.slotId === null || entry.occursOn === null) return;
  const slotId = entry.slotId;
  const occursOn = entry.occursOn;
  const group = entry.group;
  const body =
    action.kind === "move"
      ? { toDate: action.date, toTime: action.time, reason: action.reason }
      : null;
  if (group !== undefined) {
    if (body === null) await api.clearGroupOccurrence(group.id, slotId, occursOn);
    else await api.moveGroupOccurrence(group.id, slotId, occursOn, body);
    return;
  }
  if (body === null) await api.clearOccurrence(slotId, occursOn);
  else await api.moveOccurrence(slotId, occursOn, body);
}

export function DropToast({
  entry,
  landed,
  plan,
  zone,
  onChanged,
  onClose,
}: {
  /** Строка, которую взяли рукой. Из неё берётся адрес ручки и размер группы. */
  entry: ScheduleEntry;
  /** Та же строка из перечитанной недели. Из неё берутся слова итога. */
  landed: ScheduleEntry | null;
  plan: DropPlan;
  /** Зона СМОТРЯЩЕГО: в ней называется итог и в ней же живёт разовое занятие. */
  zone: string;
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<"busy" | "done" | "fail">("busy");
  const [failed, setFailed] = useState("");
  const students = entry.group?.size ?? null;

  const run = useCallback(
    async (action: DropAction, then: "done" | "close") => {
      setPhase("busy");
      try {
        await send(entry, action, zone);
        await onChanged();
        if (then === "close") onClose();
        else setPhase("done");
      } catch (e) {
        setFailed(dropErrorText(e));
        setPhase("fail");
      }
    },
    [entry, onChanged, onClose, zone],
  );

  /**
   * Запрос уходит РОВНО ОДИН раз на жест.
   *
   * ⚠️ Сторож обязателен: после успеха неделя перечитывается, строка `landed`
   * приезжает другая, и эффект без него ушёл бы на второй круг, повторив перенос.
   */
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void run(plan.action, "done");
  }, [plan.action, run]);

  /**
   * Полоса гаснет сама, но только после успеха.
   *
   * ⚠️ Таймер заводится на переход в «вышло», а не на монтирование: пока запрос
   * в полёте на слабом канале, отсчёт съел бы всё время, отведённое на «Вернуть».
   */
  useEffect(() => {
    if (phase !== "done") return;
    const id = setTimeout(onClose, DONE_MS);
    return () => {
      clearTimeout(id);
    };
  }, [onClose, phase]);

  /**
   * Возврат фокуса на коробку занятия.
   *
   * ⚠️ Фокус полоса не забирает, но занятие после переноса перерисовано другим
   * узлом, и тот, что был под курсором, из разметки исчез: фокус в этот момент
   * лежит на `body`. Коробка ищется по разметке, отложено на такт, и только если
   * фокус никто не забрал, ровно как у карточки занятия.
   */
  const key = entry.key;
  useEffect(
    () => () => {
      setTimeout(() => {
        const active = document.activeElement;
        if (active !== null && active !== document.body) return;
        const box = document.querySelector<HTMLElement>(`.cal__ev[data-entry="${key}"]`);
        box?.focus({ preventScroll: true });
      }, 0);
    },
    [key],
  );

  /** Escape убирает полосу, как любое сообщение, которое не держат в руках. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const note = landed === null ? "" : markNote(landed.mark);
  const back = plan.action.kind === "clear";

  return (
    <div
      className="toast"
      role={phase === "fail" ? "alert" : "status"}
      aria-live={phase === "fail" ? "assertive" : "polite"}
    >
      {/*
        Пока запрос в полёте, полоса стоит со словом. Молчание тут дороже, чем
        кажется: на узком канале ответа ждут секундами, занятие всё это время
        стоит на старом месте, и человек решает, что жест не сработал, и тащит
        второй раз
      */}
      {phase === "busy" && <p className="muted">Переносим…</p>}
      {phase === "fail" && <p className="err">{failed}</p>}
      {phase === "done" && (
        <>
          <p>{dropDoneText(landed, zone, plan.action)}</p>
          {/*
            Цена группы называется ПОСЛЕ, потому что ступени «до» у жеста больше
            нет (решение владельца 10.09.2026, разбор в шапке файла). При
            возврате она не повторяется: занятие уже стоит там, где стояло
          */}
          {students !== null && !back && <p className="note">{movedGroupOccurrenceText(students)}</p>}
          {note !== "" && <p className="note">{note}</p>}
          {/*
            Накладку сервер разрешает и будет разрешать: два занятия в одно время
            это законная ситуация. Но жест сделал попадание поверх чужого занятия
            не сложнее, чем рядом (коробки на время жеста не ловят указатель),
            поэтому итог называет её словами
          */}
          {(landed?.warnings ?? []).map((w) => (
            <p className="note" key={`${w.code}${String(w.withAt)}`}>
              {overlapNote(w, zone)}
            </p>
          ))}
        </>
      )}
      {phase !== "busy" && (
        <div className="toast__actions">
          {phase === "done" && (
            <button type="button" className="btn" onClick={() => void run(plan.undo, "close")}>
              Вернуть
            </button>
          )}
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            Закрыть
          </button>
        </div>
      )}
    </div>
  );
}
